import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  SimpleGrid,
  Select,
  Input,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Button,
} from "@chakra-ui/react";
import { message, Result } from "antd";
import { Controller } from "Controller/Controller";

const BasicInformation = () => {
  // Get URL query parameters
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialBusinessName =
    (searchParams.get("business_name") != "None" &&
      searchParams.get("business_name")) ||
    "";
  const initialEmail =
    (searchParams.get("email") != "None" && searchParams.get("email")) || "";
  const initialPhone =
    (searchParams.get("phone") != "None" && searchParams.get("phone")) || "";
  const uuid = searchParams.get("uuid") || "";

  // State for form data, prefilled with URL values
  const [formData, setFormData] = useState({
    business_name: initialBusinessName,
    email: initialEmail,
    phone: initialPhone,

    address: "",
    city: "",
    state: "",
    zip_code: "",
  });

  // State for profile picture file and preview
  const [profilePicture, setProfilePicture] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  // State for validation errors
  const [errors, setErrors] = useState({});

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle file input change for profile picture
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePicture(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setProfilePicture(null);
      setPreviewUrl("");
    }
  };

  const [isComplete, setIsComplete] = useState(false);
  const [loading, setLoading] = useState(false);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    let newErrors = {};
    setErrors({});
    console.log(formData);
    // Validation checks
    if (!formData.business_name)
      newErrors.business_name = "Business name is required";

    if (!formData.address) newErrors.address = "Address is required";
    if (!formData.city) newErrors.city = "City is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.zip_code) newErrors.zip_code = "Zip code is required";

    console.log(newErrors);
    setErrors(newErrors);

    // If no errors, proceed with submission
    if (Object.keys(newErrors).length === 0) {
      const data = new FormData();

      data.append("business_name", formData.business_name);

      data.append("email", formData.email);
      data.append("phone", formData.phone);
      data.append("address", formData.address);
      data.append("city", formData.city);
      data.append("state", formData.state);
      data.append("contact_first_name", formData.contact_first_name);
      data.append("contact_last_name", formData.contact_last_name);
      data.append("contact_email", formData.contact_email);
      data.append("contact_phone", formData.contact_phone);
      // data.append(
      //   "contact_person",
      //   JSON.stringify({
      //     email: formData.contact_email,
      //     phone: formData.contact_phone,
      //     first_name: formData.contact_first_name,
      //     last_name: formData.contact_last_name,
      //   })
      // );
      if (profilePicture) {
        data.append("profile_picture", profilePicture);
      }

      try {
        const response = await Controller.UpdateBasicInfo(data, uuid);

        // Show success notification
        if (response.status < 250) {
          console.log(response);
          setIsComplete(true);
          message.success("Form submitted successfully!");
        } else {
          console.log(response);
          const errorsField = {};
          if (response.json) {
            Object.keys(response.json).forEach((key) => {
              console.log(response.json[key][0]);

              errorsField[key] = response.json[key][0]; // Take the first error message
            });
          }
          console.log(errorsField);

          setErrors(errorsField);

          message.error(
            response.json?.address
              ? "address: " + response.json?.address
              : response.json?.business_name
              ? "business_name: " + response.json?.business_name
              : response.json?.email
              ? "email: " + response.json?.email
              : response.json?.phone
              ? "phone: " + response.json?.phone
              : response.json?.address
              ? "address: " + response.json?.address
              : response.json?.city
              ? "city: " + response.json?.city
              : response.json?.state
              ? "state: " + response.json?.state
              : response.json?.zip_code
              ? "zip_code: " + response.json?.zip_code
              : "Something went wrong."
          );
        }
      } catch (e) {
        message.error("Server Error.");
      }
    }

    setLoading(false);
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <SimpleGrid>
        <Box bg="white" p={5} borderRadius="md" boxShadow="md" margin="5%">
          {isComplete ? (
            <>
              <Result
                status="success"
                title="Successfully Updated Basic Information!"
                subTitle={
                  "Dear " +
                  formData.business_name +
                  ", your basic information updated successfully!"
                }
              />
            </>
          ) : (
            <>
              <Heading as="h2" size="md" mb={3}>
                Basic Information
              </Heading>
              <Heading
                as="h2"
                size="sm"
                mb={6}
                style={{ color: "gray", fontSize: "14px", fontWeight: "400" }}
              >
                Complete your basic information.
              </Heading>
              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  {/* Business Information Section */}
                  <div className="custom-divider">
                    <span
                      style={{ fontSize: "16px", color: "rgba(0,0,0,0.68)" }}
                    >
                      Business Information
                    </span>
                    <div className="line"></div>
                  </div>
                  {/* Business Name */}
                  <FormControl isInvalid={errors.business_name}>
                    <FormLabel><span style={{ color: 'red', marginRight: '4px' }}>*</span> Business Name</FormLabel>
                    <Input
                      name="business_name"
                      placeholder="Business Name"
                      value={formData.business_name}
                      onChange={handleChange}
                    />
                    <FormErrorMessage>{errors.business_name}</FormErrorMessage>
                  </FormControl>
                  {/* New Email Field */}
                  <FormControl isInvalid={errors.email}>
                    <FormLabel><span style={{ color: 'red', marginRight: '4px' }}>*</span> Email</FormLabel>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                  </FormControl>

                  {/* New Phone Field */}
                  <FormControl isInvalid={errors.phone}>
                    <FormLabel><span style={{ color: 'red', marginRight: '4px' }}>*</span> Phone</FormLabel>
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="Phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                    <FormErrorMessage>{errors.phone}</FormErrorMessage>
                  </FormControl>

                  {/* Address */}
                  <FormControl isInvalid={errors.address}>
                    <FormLabel>Address</FormLabel>
                    <Input
                      name="address"
                      placeholder="Address"
                      value={formData.address}
                      onChange={handleChange}
                    />
                    <FormErrorMessage>{errors.address}</FormErrorMessage>
                  </FormControl>

                  {/* City */}
                  <FormControl isInvalid={errors.city}>
                    <FormLabel>City</FormLabel>
                    <Input
                      name="city"
                      placeholder="City"
                      value={formData.city}
                      onChange={handleChange}
                    />
                    <FormErrorMessage>{errors.city}</FormErrorMessage>
                  </FormControl>

                  {/* State */}
                  <FormControl isInvalid={errors.state}>
                    <FormLabel>State</FormLabel>
                    <Input
                      name="state"
                      placeholder="State"
                      value={formData.state}
                      onChange={handleChange}
                    />

                    <FormErrorMessage>{errors.state}</FormErrorMessage>
                  </FormControl>

                  {/* Zip Code */}
                  <FormControl isInvalid={errors.zip_code}>
                    <FormLabel>Zip Code</FormLabel>
                    <Input
                      name="zip_code"
                      placeholder="Zip Code"
                      value={formData.zip_code}
                      onChange={handleChange}
                    />
                    <FormErrorMessage>{errors.zip_code}</FormErrorMessage>
                  </FormControl>

                  {/* Profile Picture */}
                  <FormControl>
                    <FormLabel>Profile Picture (Optional)</FormLabel>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                    {previewUrl && (
                      <img
                        src={previewUrl}
                        alt="Profile Preview"
                        style={{
                          width: "100px",
                          height: "100px",
                          marginTop: "10px",
                        }}
                      />
                    )}
                  </FormControl>

                  {/* Contact Person Section */}
                  <div className="custom-divider">
                    <span
                      style={{ fontSize: "16px", color: "rgba(0,0,0,0.68)" }}
                    >
                      Contact Person
                    </span>
                    <div className="line"></div>
                  </div>

                  <FormControl isInvalid={errors.business_name}>
                    <FormLabel><span style={{ color: 'red', marginRight: '4px' }}>*</span> First Name</FormLabel>
                    <Input
                      name="contact_first_name"
                      placeholder="First Name"
                      value={formData.contact_first_name}
                      onChange={handleChange}
                    />
                    <FormErrorMessage>
                      {errors.contact_first_name}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.contact_last_name}>
                    <FormLabel><span style={{ color: 'red', marginRight: '4px' }}>*</span> Last Name</FormLabel>
                    <Input
                      name="contact_last_name"
                      placeholder="Last Name"
                      value={formData.contact_last_name}
                      onChange={handleChange}
                    />
                    <FormErrorMessage>
                      {errors.contact_last_name}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.contact_email}>
                    <FormLabel><span style={{ color: 'red', marginRight: '4px' }}>*</span> Email</FormLabel>
                    <Input
                      name="contact_email"
                      placeholder="Email"
                      value={formData.contact_email}
                      onChange={handleChange}
                    />
                    <FormErrorMessage>{errors.contact_email}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.contact_phone}>
                    <FormLabel><span style={{ color: 'red', marginRight: '4px' }}>*</span> Phone</FormLabel>
                    <Input
                      name="contact_phone"
                      placeholder="Phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                    />
                    <FormErrorMessage>{errors.contact_phone}</FormErrorMessage>
                  </FormControl>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    colorScheme="blue"
                    width="full"
                    isLoading={loading}
                  >
                    Submit
                  </Button>
                </VStack>
              </form>
            </>
          )}
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default BasicInformation;
